// Referral / preferred-driver pairing for the web onboarding funnel.
//
// A passenger scans a driver's QR on the street and lands on /d/<code> with no
// account. The whole flow — see the driver, sign up, get paired — happens in
// the browser, so nothing here may assume a session until the bind step.
//
// Both calls go over plain HTTP rather than MQTT: @TopicController on the
// dispatch service registers an equivalent POST route for every topic (the
// same route the mobile app uses for createRideRequest), and the browser has
// no connection id to publish with before sign-in.

import { getRideApiV1 } from "./apis/api";

const PENDING_KEY = "pendingReferralCode";
/** Codes older than this are stale — most likely a different driver's card. */
const PENDING_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface ReferralDriver {
  name?: string;
  profileImage?: string;
}

/** Display form shown on the page and printed on the driver's card. */
export const formatReferralCode = (code: string): string =>
  `BEAM-${(code || "").toUpperCase()}`;

/**
 * Look up who a code belongs to. Unauthenticated — this is the only endpoint
 * on the dispatch service that does not need a session, and it returns just a
 * display name and photo.
 */
export const resolveReferralDriver = async (
  code: string
): Promise<ReferralDriver | null> => {
  try {
    const response = await getRideApiV1().get(
      `/referral/${encodeURIComponent(code)}`
    );
    const body = await response.json();
    return body?.found && body?.driver ? body.driver : null;
  } catch (e) {
    console.warn("[referral] resolve failed", e);
    return null;
  }
};

/**
 * Bind the driver as the signed-in passenger's preferred driver.
 *
 * The path mirrors the topic the dispatch service subscribes to, with its
 * wildcards replaced by `wc-plus` — that is what @TopicController maps the
 * MQTT topic onto for HTTP.
 */
export const bindPreferredDriver = async (params: {
  userId: string;
  code: string;
  cudbServiceId?: string | null;
}): Promise<{ ok: boolean; error?: string }> => {
  const ecoSystem = process.env.REACT_APP_ECO_SYSTEM || "beamdevlive";
  try {
    const response = await getRideApiV1().postJson(
      `/${ecoSystem}/connection/wc-plus/service/wc-plus/setPreferredDriver`,
      {
        userId: params.userId,
        code: params.code,
        source: "qr",
        cudbServiceId: params.cudbServiceId || undefined,
      } as any
    );
    const body = await response.json();
    if (body?.status && body?.data?.binding) return { ok: true };
    return {
      ok: false,
      error: body?.data?.details || body?.message || "Could not save your driver.",
    };
  } catch (e: any) {
    console.warn("[referral] bind failed", e);
    return { ok: false, error: e?.message || "Could not reach the server." };
  }
};

// ── Pending code, carried across the signup redirect ────────────────────────
// The passenger leaves /d/<code> for the register/verify screens and comes
// back authenticated, so the code has to survive those navigations. sessionStorage
// (not localStorage) keeps it scoped to the tab and self-cleaning.

interface PendingRecord {
  code: string;
  at: number;
}

export const setPendingReferralCode = (code: string): void => {
  if (!code) return;
  try {
    const record: PendingRecord = { code, at: Date.now() };
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(record));
  } catch (e) {
    // Private browsing can refuse storage; the code stays in the URL, so the
    // passenger can still complete the flow by returning to the link.
  }
};

export const clearPendingReferralCode = (): void => {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch (e) {
    /* ignore */
  }
};

export const getPendingReferralCode = (): string | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const record: PendingRecord = JSON.parse(raw);
    if (!record?.code) return null;
    if (Date.now() - (record.at || 0) > PENDING_TTL_MS) {
      clearPendingReferralCode();
      return null;
    }
    return record.code;
  } catch (e) {
    clearPendingReferralCode();
    return null;
  }
};
